import { useRegisterEnvironmentCommands } from "@/actions/use-register-environment-commands";
import { VSCodeBrowserView } from "@/components/ai/chat/AgentExecutionConversation";
import type { EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import { EnvironmentBanner } from "@/components/environments/EnvironmentBanner";
import { EnvironmentDetailsTopHeader } from "@/components/environments/EnvironmentDetailsTopHeader";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { cn } from "@/components/podkit/lib/cn";
import { MainContent } from "@/components/SidebarLayout";
import { VSCodeIpcProvider } from "@/contexts/VSCodeIpcContext";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import type { AnnotationSyncHandle } from "@/hooks/use-agent-annotation-sync";
import { useEnsureVscodeServer } from "@/hooks/use-ensure-vscode-server";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import type { EnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import { useEnvironmentViewMode } from "@/hooks/use-environment-view-mode";
import {
    useFlagCommandPaletteEnabled,
    useFlagOnaAIEnabled,
    useFlagOnaEnvironmentOnboardingEnabled,
    useFlagIsMobileVscodeEnabled,
    useFlagReviewCommentsEnabled,
    useFlagSuperOnaEnabled,
} from "@/hooks/use-flag";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { useEditorURLQuery } from "@/queries/editor-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { EnvironmentAgentExecutionConversation } from "@/routes/environments/details/EnvironmentAgentExecutionConversation";

import {
    EnvironmentChangesView,
    type EnvironmentChangesViewRef,
} from "@/routes/environments/details/EnvironmentChangesView";
import { type CommentCountByFile } from "@/routes/environments/details/file-tree-utils";
import type { FileDiffReviewComments } from "@/routes/environments/details/SuperOnaFileDiffPanel";
import { useReviewComments, type ReviewCommentsHandle } from "@/components/ai/review/use-review-comments";

import { useIsWelcomeEligible } from "@/hooks/use-welcome-eligible";
import { hasLLMCapabilities } from "@/utils/runner-capabilities";

import type React from "react";
import { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import type { ImperativePanelGroupHandle, ImperativePanelHandle } from "react-resizable-panels";
import { getPanelElement, getPanelGroupElement } from "react-resizable-panels";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { asNavState } from "@/routes/environments/details/environment-nav-state";

const LazySuperOnaEnvironmentContent = lazy(() =>
    import("@/routes/environments/details/SuperOnaEnvironmentContent").then((m) => ({
        default: m.SuperOnaEnvironmentContent,
    })),
);

const LazySuperOnaEnvironmentDetailsPanel = lazy(() =>
    import("@/routes/environments/details/SuperOnaEnvironmentDetailsPanel").then((m) => ({
        default: m.SuperOnaEnvironmentDetailsPanel,
    })),
);

export const EnvironmentDetailsLayout: FC = () => {
    useDynamicTitle("Environment");
    useEnsureVscodeServer();

    const { environmentId = "" } = useParams();
    const { value: commandPaletteEnabled } = useFlagCommandPaletteEnabled();
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const scope = useMemo(() => ({ case: "environmentId" as const, value: environmentId }), [environmentId]);
    useStreamEvents(scope);
    const { environmentConversation } = useEnvironmentConversation(environmentId);
    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();

    const isFeatureFlagEnabled = isOnaEnabled || isOnaEnvironmentOnboardingEnabled;
    const runnerSupportsLLM = hasLLMCapabilities(runner);

    const shouldShowConversationView = isFeatureFlagEnabled && runnerSupportsLLM;

    if (shouldShowConversationView) {
        return (
            <VSCodeIpcProvider>
                {commandPaletteEnabled && <EnvironmentCommandPaletteRegistration environmentId={environmentId} />}
                <EnvironmentDetailsWithConversation
                    key={environmentId}
                    environmentConversation={environmentConversation}
                    environmentId={environmentId}
                />
            </VSCodeIpcProvider>
        );
    }

    // Show regular details page when feature flags are disabled OR runner lacks LLM capabilities
    return (
        <>
            {commandPaletteEnabled && <EnvironmentCommandPaletteRegistration environmentId={environmentId} />}
            <MainContent>
                <Suspense>
                    <VSCodeIpcProvider>
                        <EnvironmentDetailsLayoutOutlet context={{ showingAgentConversation: false }} />
                    </VSCodeIpcProvider>
                </Suspense>
            </MainContent>
        </>
    );
};

const EnvironmentCommandPaletteRegistration: FC<{ environmentId: string }> = ({ environmentId }) => {
    useRegisterEnvironmentCommands(environmentId);
    return null;
};

type EnvironmentAgentExecutionConversationPanelProps = {
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    environmentId: string;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    onBuildButtonAppear?: (specFilePath: string | null) => void;
    selectedFilePath?: string | null;
    onFileSelect?: (filePath: string | null) => void;
    reviewComments?: FileDiffReviewComments;
    annotationSyncHandle?: AnnotationSyncHandle;
};

const EnvironmentAgentExecutionConversationPanel: FC<EnvironmentAgentExecutionConversationPanelProps> = ({
    environmentConversation,
    environmentId,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    onBuildButtonAppear,
    selectedFilePath,
    onFileSelect,
    reviewComments,
    annotationSyncHandle,
}) => {
    const agentExecution = getAgentExecution(environmentConversation);
    const { value: isSuperOnaEnabled } = useFlagSuperOnaEnabled();
    const key = agentExecution?.id || `empty-${environmentId}`;

    if (isSuperOnaEnabled) {
        return (
            <Suspense fallback={null}>
                <LazySuperOnaEnvironmentContent
                    key={key}
                    agentExecution={agentExecution}
                    isDetailsPanelOpen={isDetailsPanelOpen}
                    onToggleDetailsPanel={onToggleDetailsPanel}
                    environmentId={environmentId}
                    onBuildButtonAppear={onBuildButtonAppear}
                    selectedFilePath={selectedFilePath}
                    onFileSelect={onFileSelect}
                    reviewComments={reviewComments}
                    annotationSyncHandle={annotationSyncHandle}
                />
            </Suspense>
        );
    }

    return (
        <EnvironmentAgentExecutionConversation
            key={key}
            agentExecution={agentExecution}
            isDetailsPanelOpen={isDetailsPanelOpen}
            onToggleDetailsPanel={onToggleDetailsPanel}
            environmentId={environmentId}
            onBuildButtonAppear={onBuildButtonAppear}
        />
    );
};

/**
 * Shared state for both EnvironmentDetailsPanel and SuperOnaEnvironmentDetailsPanel:
 * environment/runner data, view mode, review comments flag, and parent-driven
 * view mode / notification effects.
 */
function useDetailsPanelState(
    environmentId: string,
    onCurrentViewMode: ((mode: EnvironmentViewMode) => void) | undefined,
    requestedViewMode: EnvironmentViewMode | null | undefined,
    onRequestedViewModeConsumed: (() => void) | undefined,
) {
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const [viewMode, setViewMode] = useEnvironmentViewMode(environment);
    const { value: isReviewCommentsEnabled } = useFlagReviewCommentsEnabled();

    // Notify parent of current view mode
    useEffect(() => {
        onCurrentViewMode?.(viewMode);
    }, [viewMode, onCurrentViewMode]);

    // Consume parent-driven view mode changes
    useEffect(() => {
        if (!requestedViewMode) return;
        setViewMode(requestedViewMode);
        onRequestedViewModeConsumed?.();
    }, [requestedViewMode, setViewMode, onRequestedViewModeConsumed]);

    return { environment, runner, viewMode, setViewMode, isReviewCommentsEnabled };
}

const EnvironmentDetailsPanel: FC<{
    environmentId: string;
    showConversationPanel: boolean;
    onToggleDetailsPanel?: () => void;
    onToggleConversationPanel?: () => void;
    isConversationPanelOpen?: boolean;
    onViewModeChange?: (mode: EnvironmentViewMode) => void;
    onCurrentViewMode?: (mode: EnvironmentViewMode) => void;
    changesViewRef?: React.RefObject<EnvironmentChangesViewRef>;
    pendingSpecPreview?: string | null;
    onPendingSpecPreviewConsumed?: () => void;
    requestedViewMode?: EnvironmentViewMode | null;
    onRequestedViewModeConsumed?: () => void;
    reviewCommentsHandle?: ReviewCommentsHandle;
}> = ({
    environmentId,
    showConversationPanel,
    onToggleDetailsPanel,
    onToggleConversationPanel,
    isConversationPanelOpen = true,
    onViewModeChange,
    onCurrentViewMode,
    changesViewRef,
    pendingSpecPreview,
    onPendingSpecPreviewConsumed,
    requestedViewMode,
    onRequestedViewModeConsumed,
    reviewCommentsHandle,
}) => {
    const { environment, runner, viewMode, setViewMode, isReviewCommentsEnabled } = useDetailsPanelState(
        environmentId,
        onCurrentViewMode,
        requestedViewMode,
        onRequestedViewModeConsumed,
    );

    const { availableEditors } = useGetEditorForEnv(environment?.id);
    const isVSCodeBrowserAllowed = useMemo(() => {
        const hasVSCodeBrowser = availableEditors?.some((editor) => editor.alias === "vscode-browser") ?? false;
        const isVSCodeContext = !!window.isWebview;
        return hasVSCodeBrowser && !isVSCodeContext;
    }, [availableEditors]);
    const vscodeBrowserId = useMemo(
        () => availableEditors?.find((editor) => editor.alias === "vscode-browser")?.id,
        [availableEditors],
    );
    const editorUrlQuery = useEditorURLQuery({
        editorId: vscodeBrowserId,
        environment: environment,
    });
    const editorUrl = editorUrlQuery.data;

    const [readyToLoadVSCode, setReadyToLoadVSCode] = useState(false);

    useEffect(() => {
        if (environment && isVSCodeBrowserAllowed && !readyToLoadVSCode) {
            setReadyToLoadVSCode(true);
        }
    }, [environment, isVSCodeBrowserAllowed, readyToLoadVSCode]);

    useEffect(() => {
        if (viewMode === "vscode" && !isVSCodeBrowserAllowed) {
            setViewMode("details");
        }
    }, [viewMode, isVSCodeBrowserAllowed, setViewMode]);

    // Switch view mode when navigating via command palette or other triggers.
    const location = useLocation();
    const navState = asNavState(location.state);
    useEffect(() => {
        if (navState?.switchToVscode) {
            setViewMode("vscode");
        } else if (navState?.switchToDetails) {
            setViewMode("details");
        } else {
            return;
        }
        // Clear state so browser back/forward doesn't re-trigger.
        window.history.replaceState({}, "", location.pathname);
    }, [navState?.switchToDetails, navState?.switchToVscode, setViewMode, location.pathname]);

    const _onViewModeChange = useCallback(
        (mode: EnvironmentViewMode) => {
            setViewMode(mode);
            if (onViewModeChange) {
                onViewModeChange(mode);
            }
        },
        [setViewMode, onViewModeChange],
    );

    return (
        <div className="flex h-full max-h-full flex-col">
            {!window.isWebview && (
                <EnvironmentDetailsTopHeader
                    environment={environment}
                    viewMode={viewMode}
                    onViewModeChange={_onViewModeChange}
                    onToggleDetailsPanel={onToggleDetailsPanel}
                    onToggleConversationPanel={onToggleConversationPanel}
                    isConversationPanelOpen={isConversationPanelOpen}
                    isVSCodeBrowserAllowed={isVSCodeBrowserAllowed}
                />
            )}
            <EnvironmentBanner environment={environment} runner={runner} />

            {viewMode === "details" && (
                <ScrollArea orientation="vertical" className="mx-[2px] my-[2px] h-full gap-6 p-4 md:p-6">
                    <Suspense>
                        <EnvironmentDetailsLayoutOutlet context={{ showingAgentConversation: showConversationPanel }} />
                    </Suspense>
                </ScrollArea>
            )}

            {viewMode === "changes" && (
                <ScrollArea orientation="vertical" className="mx-[2px] my-[2px] flex h-full flex-col gap-6 p-4 md:p-6">
                    <EnvironmentChangesView
                        ref={changesViewRef}
                        environmentId={environmentId}
                        enableReviewComments={isReviewCommentsEnabled}
                        pendingSpecPreview={pendingSpecPreview}
                        onPendingSpecPreviewConsumed={onPendingSpecPreviewConsumed}
                        reviewComments={reviewCommentsHandle}
                    />
                </ScrollArea>
            )}

            <div
                className={cn(
                    "size-full grow flex-col border-b border-l-0 border-r border-t-0 border-border-base",
                    viewMode === "vscode" ? "flex" : "hidden",
                )}
                data-vscode-visible={viewMode === "vscode"}
            >
                {readyToLoadVSCode && (
                    <VSCodeBrowserView
                        editorUrl={editorUrl?.url}
                        className="grow border-none"
                        visible={viewMode === "vscode"}
                    />
                )}
            </div>
        </div>
    );
};

const EnvironmentDetailsWithConversation: FC<{
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    environmentId: string;
}> = ({ environmentConversation, environmentId }) => {
    // Simple rule: track if code view is expanded per environment
    // Expanded code view = conversation hidden, everything else = conversation visible
    const getCodeViewExpanded = useCallback(() => {
        const key = `environment-code-expanded:${environmentId}`;
        return localStorage.getItem(key) === "true";
    }, [environmentId]);

    const setCodeViewExpanded = useCallback(
        (expanded: boolean) => {
            const key = `environment-code-expanded:${environmentId}`;
            localStorage.setItem(key, String(expanded));
        },
        [environmentId],
    );

    const [isConversationPanelCollapsed, setIsConversationPanelCollapsed] = useState(false);
    const showConversationPanel = !isConversationPanelCollapsed;
    const [currentViewMode, setCurrentViewMode] = useState<EnvironmentViewMode>("details");

    // File selected from the right-panel tree — opens a diff tab in the left panel
    const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
    const handleFileSelect = useCallback((filePath: string | null) => {
        setSelectedFilePath(filePath);
    }, []);

    // Single source of truth for review comments — shared between the file diff
    // tab (left panel), the file tree comment counters (right panel), and the
    // submit review button in the right panel header.
    const {
        comments: reviewComments,
        commentsByFile,
        clearAllComments,
        addComment,
        updateComment,
        removeComment,
        setCommentThumbsUp,
        setCommentThumbsDown,
        isAnnotationDismissed,
    } = useReviewComments(environmentId);

    // Handle passed to EnvironmentAgentExecutionConversation so it can call
    // useAgentAnnotationSync with the stream messages it already owns.
    const annotationSyncHandle: AnnotationSyncHandle = useMemo(
        () => ({ comments: reviewComments, addComment, isAnnotationDismissed }),
        [reviewComments, addComment, isAnnotationDismissed],
    );

    // Handle passed to the file diff tab so it shares the same comment state.
    // Not gated on the review-comments flag here — SuperOnaFileDiffPanel decides whether
    // to enable the comment UI based on whether this handle is provided, and the
    // flag is already checked by the parent when deciding whether to render the
    // submit-review button.
    const fileDiffReviewComments: FileDiffReviewComments = useMemo(
        () => ({ commentsByFile, addComment, updateComment, removeComment, setCommentThumbsUp, setCommentThumbsDown }),
        [commentsByFile, addComment, updateComment, removeComment, setCommentThumbsUp, setCommentThumbsDown],
    );
    const commentCountByFile: CommentCountByFile = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const [filePath, comments] of Object.entries(commentsByFile)) {
            if (comments && comments.length > 0) {
                counts[filePath] = comments.length;
            }
        }
        return counts;
    }, [commentsByFile]);

    // Handle passed to EnvironmentDetailsPanel → EnvironmentChangesView →
    // CodeChangesPanel so the non-super_ona changes view shares the same
    // comment state instead of creating a second useReviewComments instance.
    const codeChangesReviewComments: ReviewCommentsHandle = useMemo(
        () => ({
            comments: reviewComments,
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            setCommentThumbsUp,
            setCommentThumbsDown,
        }),
        [
            reviewComments,
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            setCommentThumbsUp,
            setCommentThumbsDown,
        ],
    );

    const [fixedConversationPanelSize, setFixedConversationPanelSize] = useState(0);
    const conversationPanelRef = useRef<ImperativePanelHandle>(null);
    const changesViewRef = useRef<EnvironmentChangesViewRef>(null);
    const panelGroupId = useId();
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
    const panelRef = useRef<ImperativePanelHandle>(null);
    const isWelcomeEligible = useIsWelcomeEligible();
    const [isDetailsPanelCollapsed, setIsDetailsPanelCollapsed] = useState(false);
    const hasAutoCollapsedForWelcome = useRef(false);

    // Collapse the details panel for welcome-eligible users so the
    // conversation panel gets full width on first load.
    useEffect(() => {
        if (isWelcomeEligible && !hasAutoCollapsedForWelcome.current && panelRef.current) {
            hasAutoCollapsedForWelcome.current = true;
            requestAnimationFrame(() => {
                panelRef.current?.collapse();
            });
        }
    }, [isWelcomeEligible]);
    const updatePanelSizeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const conversationPanelSize = showConversationPanel
        ? { defaultSize: 70, minSize: 30 }
        : { defaultSize: 0, minSize: 0 };

    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { value: isMobileVscodeEnabled } = useFlagIsMobileVscodeEnabled();
    const { value: isSuperOnaEnabled } = useFlagSuperOnaEnabled();

    // The library doesn't support animating the collapse/expand transition of panels [1]
    // You achieve it using CSS transitions, however, that breaks drag resizing behavior/performance
    // so the workaround is to only add those transitions when not dragging.
    //
    // [1] https://github.com/bvaughn/react-resizable-panels/issues/310#issuecomment-1974779047
    const [isDragging, setIsDragging] = useState(false);

    useEffect(
        () => () => {
            if (updatePanelSizeTimeout.current) {
                clearTimeout(updatePanelSizeTimeout.current);
            }
        },
        [],
    );

    // Ensure conversation is always visible when in details or changes mode
    useEffect(() => {
        if ((currentViewMode === "details" || currentViewMode === "changes") && isConversationPanelCollapsed) {
            if (conversationPanelRef.current?.isCollapsed()) {
                conversationPanelRef.current.expand();
            }
            setIsConversationPanelCollapsed(false);
        }
    }, [currentViewMode, isConversationPanelCollapsed]);

    const handleToggleDetailsPanel = useCallback(() => {
        if (!panelRef.current) {
            return;
        }

        setFixedConversationPanelSize(0);

        if (panelRef.current.isCollapsed()) {
            panelRef.current.expand();
        } else {
            panelRef.current.collapse();
        }
    }, [panelRef]);

    const handleToggleConversationPanel = useCallback(() => {
        if (!conversationPanelRef.current) {
            return;
        }

        const willBeExpanded = conversationPanelRef.current.isCollapsed();

        if (willBeExpanded) {
            // Minimizing code view - show conversation
            conversationPanelRef.current.expand();
            setIsConversationPanelCollapsed(false);
            setCodeViewExpanded(false);
        } else {
            // Expanding code view - hide conversation
            conversationPanelRef.current.collapse();
            setIsConversationPanelCollapsed(true);
            setCodeViewExpanded(true);
        }
    }, [setCodeViewExpanded]);

    const onViewModeChange = useCallback(
        (mode: EnvironmentViewMode) => {
            setCurrentViewMode(mode);
            switch (mode) {
                case "details":
                case "changes": {
                    // Switching to details/changes view = minimizing code view = show conversation
                    if (conversationPanelRef.current?.isCollapsed()) {
                        conversationPanelRef.current.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    setCodeViewExpanded(false);
                    break;
                }
                case "vscode": {
                    // Switching to code view - restore per-environment expanded state
                    const isExpanded = getCodeViewExpanded();
                    const isCurrentlyCollapsed = conversationPanelRef.current?.isCollapsed() ?? false;

                    if (isExpanded && !isCurrentlyCollapsed) {
                        // Should be expanded (conversation hidden)
                        conversationPanelRef.current?.collapse();
                        setIsConversationPanelCollapsed(true);
                    } else if (!isExpanded && isCurrentlyCollapsed) {
                        // Should not be expanded (conversation visible)
                        conversationPanelRef.current?.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    break;
                }
            }

            const id = conversationPanelRef.current?.getId();
            const conversationPanelElement = document.getElementById(id ?? "");
            if (mode === "vscode" && conversationPanelElement) {
                setFixedConversationPanelSize(conversationPanelElement.clientWidth);
                // wait for sidebar to finish animating out, then calculate update the panel proportion imperatively
                updatePanelSizeTimeout.current = setTimeout(() => {
                    const groupElement = getPanelGroupElement(panelGroupId);
                    const id = conversationPanelRef.current?.getId();
                    const conversationPanelElement = getPanelElement(id ?? "");
                    if (groupElement && conversationPanelElement && panelGroupRef.current) {
                        const proportionalSize =
                            (conversationPanelElement.clientWidth / groupElement.clientWidth) * 100;
                        panelGroupRef.current.setLayout([proportionalSize, 100 - proportionalSize]);
                    }
                }, 250);
            }
        },
        [getCodeViewExpanded, setCodeViewExpanded, panelGroupId],
    );

    const [pendingSpecPreview, setPendingSpecPreview] = useState<string | null>(null);
    const clearPendingSpecPreview = useCallback(() => setPendingSpecPreview(null), []);

    const [requestedViewMode, setRequestedViewMode] = useState<EnvironmentViewMode | null>(null);
    const clearRequestedViewMode = useCallback(() => setRequestedViewMode(null), []);

    const handleBuildButtonAppear = useCallback(
        (specFilePath: string | null) => {
            if (isMobile) return;

            if (panelRef.current?.isCollapsed()) {
                panelRef.current.expand();
            }

            onViewModeChange("changes");
            setRequestedViewMode("changes");

            if (specFilePath) {
                setPendingSpecPreview(specFilePath);
            }
        },
        [isMobile, onViewModeChange, panelRef],
    );

    const onDragging = (isDragging: boolean) => {
        setIsDragging(isDragging);
        if (fixedConversationPanelSize) {
            setFixedConversationPanelSize(0);
        }
    };

    // Only auto-collapse details panel on mobile when mobile VSCode is disabled
    useEffect(() => {
        if (isMobile && !isMobileVscodeEnabled) {
            setIsDetailsPanelCollapsed(true);
        }
    }, [isMobile, isMobileVscodeEnabled]);

    const toggleDetailsPanel = useCallback(() => {
        if (isMobile) {
            setIsDetailsPanelCollapsed(true);
        } else {
            handleToggleDetailsPanel();
        }
    }, [isMobile, handleToggleDetailsPanel]);

    const toggleConversationPanel = useCallback(() => {
        if (isMobile) {
            setIsDetailsPanelCollapsed(false);
        } else {
            handleToggleConversationPanel();
        }
    }, [isMobile, handleToggleConversationPanel]);

    const conversationPanel = (
        <EnvironmentAgentExecutionConversationPanel
            environmentConversation={environmentConversation}
            environmentId={environmentId}
            isDetailsPanelOpen={isMobile ? false : !isDetailsPanelCollapsed}
            onToggleDetailsPanel={isMobile ? () => setIsDetailsPanelCollapsed(false) : handleToggleDetailsPanel}
            onBuildButtonAppear={handleBuildButtonAppear}
            selectedFilePath={selectedFilePath}
            onFileSelect={handleFileSelect}
            reviewComments={fileDiffReviewComments}
            annotationSyncHandle={annotationSyncHandle}
        />
    );

    const detailsPanel = isSuperOnaEnabled ? (
        <Suspense>
            <LazySuperOnaEnvironmentDetailsPanel
                environmentId={environmentId}
                selectedFilePath={selectedFilePath}
                onFileSelect={handleFileSelect}
                commentCountByFile={commentCountByFile}
                reviewCommentsHandle={codeChangesReviewComments}
            />
        </Suspense>
    ) : (
        <EnvironmentDetailsPanel
            environmentId={environmentId}
            showConversationPanel={showConversationPanel}
            onToggleDetailsPanel={toggleDetailsPanel}
            onToggleConversationPanel={toggleConversationPanel}
            isConversationPanelOpen={isMobile ? !isDetailsPanelCollapsed : !isConversationPanelCollapsed}
            onViewModeChange={isMobile ? undefined : onViewModeChange}
            onCurrentViewMode={setCurrentViewMode}
            changesViewRef={changesViewRef}
            pendingSpecPreview={pendingSpecPreview}
            onPendingSpecPreviewConsumed={clearPendingSpecPreview}
            requestedViewMode={requestedViewMode}
            onRequestedViewModeConsumed={clearRequestedViewMode}
            reviewCommentsHandle={codeChangesReviewComments}
        />
    );

    if (isMobile) {
        return <MainContent noPadding>{isDetailsPanelCollapsed ? conversationPanel : detailsPanel}</MainContent>;
    }

    return (
        <MainContent noPadding>
            <ResizablePanelGroup
                id={panelGroupId}
                ref={panelGroupRef}
                direction="horizontal"
                className="flex"
                autoSaveId="environment-details-layout"
            >
                <ResizablePanel
                    ref={conversationPanelRef}
                    defaultSize={conversationPanelSize.defaultSize}
                    minSize={conversationPanelSize.minSize}
                    collapsedSize={0}
                    collapsible={currentViewMode === "vscode"}
                    order={1}
                    className={cn({
                        // See definition of isDragging for why we're doing this selectively
                        "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out": !isDragging,
                    })}
                    style={
                        fixedConversationPanelSize
                            ? {
                                  maxWidth: `${fixedConversationPanelSize}px`,
                              }
                            : {}
                    }
                    onCollapse={() => setIsConversationPanelCollapsed(true)}
                    onExpand={() => setIsConversationPanelCollapsed(false)}
                    id="conversation-panel"
                >
                    {conversationPanel}
                </ResizablePanel>
                <ResizableHandle
                    disabled={isConversationPanelCollapsed || isDetailsPanelCollapsed}
                    onDragging={onDragging}
                    className="border-l-[1.5px] border-border-base"
                />
                <ResizablePanel
                    defaultSize={100 - conversationPanelSize.defaultSize}
                    minSize={30}
                    collapsedSize={0}
                    ref={panelRef}
                    collapsible
                    className={cn({
                        // See definition of isDragging for why we're doing this selectively
                        "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out": !isDragging,
                    })}
                    onCollapse={() => setIsDetailsPanelCollapsed(true)}
                    onExpand={() => setIsDetailsPanelCollapsed(false)}
                    order={2}
                    id="environment-details"
                >
                    {detailsPanel}
                </ResizablePanel>
            </ResizablePanelGroup>
        </MainContent>
    );
};

/**
 * Outlet wrapper for EnvironmentDetailsOutletContext to ensure type-safety if we update the context type.
 */
const EnvironmentDetailsLayoutOutlet: FC<{ context: EnvironmentDetailsOutletContext }> = ({ context }) => {
    return <Outlet context={context} />;
};
